import { useState, useEffect, useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle } from "lucide-react";
import { StockItem } from "@/types/stock-item";

interface StockItemQuantitiesTabProps {
  item: StockItem;
  isEditMode: boolean;
  onItemChange?: (item: StockItem) => void;
  validationErrors?: Record<string, string>;
  touchedFields?: Set<string>;
  onFieldBlur?: (field: keyof StockItem) => void;
}

export function StockItemQuantitiesTab({ item, isEditMode, onItemChange, onFieldBlur: parentOnFieldBlur }: StockItemQuantitiesTabProps) {
  const [editedItem, setEditedItem] = useState<StockItem>(item);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Update edited item when prop changes
  useEffect(() => {
    setEditedItem(item);
    setValidationErrors({});
  }, [item]);

  // Calculate free quantity
  const freeQuantity = useMemo(() => {
    return editedItem.physicalQuantity - editedItem.allocatedQuantity;
  }, [editedItem.physicalQuantity, editedItem.allocatedQuantity]);

  // Determine free quantity color
  const getFreeQuantityColor = () => {
    const buffer = editedItem.minQuantity * 0.2;
    if (freeQuantity > editedItem.minQuantity) {
      return "text-green-600 dark:text-green-400";
    } else if (freeQuantity >= editedItem.minQuantity - buffer) {
      return "text-orange-600 dark:text-orange-400";
    } else {
      return "text-red-600 dark:text-red-400";
    }
  };

  // Handle field changes
  const handleFieldChange = (field: keyof StockItem, value: any) => {
    const updatedItem = { ...editedItem, [field]: value };
    setEditedItem(updatedItem);
    onItemChange?.(updatedItem);
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      const newErrors = { ...validationErrors };
      delete newErrors[field];
      setValidationErrors(newErrors);
    }
  };

  // Validate field on blur
  const handleFieldBlur = (field: keyof StockItem) => {
    const errors: Record<string, string> = { ...validationErrors };

    if (field === "minQuantity" || field === "maxQuantity") {
      if (editedItem.minQuantity >= editedItem.maxQuantity) {
        errors.minQuantity = "Minimum Quantity must be less than Maximum Quantity";
        errors.maxQuantity = "Maximum Quantity must be greater than Minimum Quantity";
      } else {
        delete errors.minQuantity;
        delete errors.maxQuantity;
      }
    }

    if (field === "reorderPoint") {
      if (editedItem.reorderPoint < editedItem.minQuantity || editedItem.reorderPoint > editedItem.maxQuantity) {
        errors.reorderPoint = "Reorder Point must be between Minimum and Maximum Quantity";
      } else {
        delete errors.reorderPoint;
      }
    }

    setValidationErrors(errors);
    
    // Sync with parent validation state to ensure Save button re-enables
    parentOnFieldBlur?.(field);
    
    // Also notify parent about related fields when cross-field validation changes
    if (field === "minQuantity" || field === "maxQuantity") {
      parentOnFieldBlur?.("maxQuantity");
      parentOnFieldBlur?.("reorderPoint");
    }
  };

  // Calculate visual indicator positions
  const getMarkerPosition = (value: number) => {
    if (editedItem.maxQuantity === 0) return 0;
    return (value / editedItem.maxQuantity) * 100;
  };

  return (
    <div className="space-y-6">
      {/* Section 1: Current Stock Levels */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Current Stock Levels</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Physical Quantity Card - Always read-only as it can only change via stock movements */}
          <Card className="bg-muted/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Physical Quantity
              </CardTitle>
              <CardDescription className="text-xs">
                Updated via stock movements
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-muted-foreground">{editedItem.physicalQuantity}</div>
            </CardContent>
          </Card>

          {/* Allocated Quantity Card */}
          <Card className="bg-muted/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Allocated Quantity
              </CardTitle>
              <CardDescription className="text-xs">
                Quantity reserved for orders
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-muted-foreground">
                {editedItem.allocatedQuantity}
              </div>
            </CardContent>
          </Card>

          {/* Quantity Due In Card */}
          <Card className="bg-muted/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Quantity Due In
              </CardTitle>
              <CardDescription className="text-xs">
                Expected from purchase orders
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-muted-foreground">
                {editedItem.quantityDueIn}
              </div>
            </CardContent>
          </Card>

          {/* Free Quantity Card */}
          <Card className="border-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Free Quantity
              </CardTitle>
              <CardDescription className="text-xs">
                Physical - Allocated
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${getFreeQuantityColor()}`}>
                {freeQuantity}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Section 2: Visual Stock Indicator */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Stock Level Indicator</h3>
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {/* Progress Bar */}
              <div className="relative h-12 rounded-md overflow-hidden border">
                {/* Red Zone */}
                <div
                  className="absolute h-full bg-red-200 dark:bg-red-900/30"
                  style={{
                    left: 0,
                    width: `${getMarkerPosition(editedItem.minQuantity)}%`,
                  }}
                />
                {/* Orange Zone */}
                <div
                  className="absolute h-full bg-orange-200 dark:bg-orange-900/30"
                  style={{
                    left: `${getMarkerPosition(editedItem.minQuantity)}%`,
                    width: `${getMarkerPosition(editedItem.reorderPoint) - getMarkerPosition(editedItem.minQuantity)}%`,
                  }}
                />
                {/* Green Zone */}
                <div
                  className="absolute h-full bg-green-200 dark:bg-green-900/30"
                  style={{
                    left: `${getMarkerPosition(editedItem.reorderPoint)}%`,
                    width: `${100 - getMarkerPosition(editedItem.reorderPoint)}%`,
                  }}
                />

                {/* Current Stock Marker */}
                <div
                  className="absolute top-0 bottom-0 w-1 bg-primary z-10"
                  style={{
                    left: `${getMarkerPosition(editedItem.physicalQuantity)}%`,
                  }}
                >
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-semibold text-primary">
                    Current: {editedItem.physicalQuantity}
                  </div>
                </div>
              </div>

              {/* Markers Legend */}
              <div className="flex justify-between text-xs text-muted-foreground pt-2">
                <div className="flex flex-col items-start">
                  <span className="font-medium">Min</span>
                  <span>{editedItem.minQuantity}</span>
                </div>
                <div className="flex flex-col items-center">
                  <span className="font-medium">Reorder</span>
                  <span>{editedItem.reorderPoint}</span>
                </div>
                <div className="flex flex-col items-end">
                  <span className="font-medium">Max</span>
                  <span>{editedItem.maxQuantity}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Section 3: Stock Thresholds */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Stock Thresholds</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Minimum Quantity */}
          <div className="space-y-2">
            <Label htmlFor="minQuantity">Minimum Quantity</Label>
            <Input
              id="minQuantity"
              type="number"
              value={editedItem.minQuantity}
              onChange={(e) => handleFieldChange("minQuantity", parseInt(e.target.value) || 0)}
              onBlur={() => handleFieldBlur("minQuantity")}
              min="0"
              step="1"
              readOnly={!isEditMode}
              className={`${!isEditMode ? 'bg-muted border-0 cursor-default' : ''} ${
                validationErrors.minQuantity ? 'border-red-500' : ''
              }`}
            />
            <p className="text-xs text-muted-foreground">Trigger low stock alert</p>
            {validationErrors.minQuantity && (
              <div className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                <AlertCircle className="h-3 w-3" />
                <span>{validationErrors.minQuantity}</span>
              </div>
            )}
          </div>

          {/* Maximum Quantity */}
          <div className="space-y-2">
            <Label htmlFor="maxQuantity">Maximum Quantity</Label>
            <Input
              id="maxQuantity"
              type="number"
              value={editedItem.maxQuantity}
              onChange={(e) => handleFieldChange("maxQuantity", parseInt(e.target.value) || 0)}
              onBlur={() => handleFieldBlur("maxQuantity")}
              min="0"
              step="1"
              readOnly={!isEditMode}
              className={`${!isEditMode ? 'bg-muted border-0 cursor-default' : ''} ${
                validationErrors.maxQuantity ? 'border-red-500' : ''
              }`}
            />
            <p className="text-xs text-muted-foreground">Maximum storage capacity</p>
            {validationErrors.maxQuantity && (
              <div className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                <AlertCircle className="h-3 w-3" />
                <span>{validationErrors.maxQuantity}</span>
              </div>
            )}
          </div>

          {/* Reorder Point */}
          <div className="space-y-2">
            <Label htmlFor="reorderPoint">Reorder Point</Label>
            <Input
              id="reorderPoint"
              type="number"
              value={editedItem.reorderPoint}
              onChange={(e) => handleFieldChange("reorderPoint", parseInt(e.target.value) || 0)}
              onBlur={() => handleFieldBlur("reorderPoint")}
              min="0"
              step="1"
              readOnly={!isEditMode}
              className={`${!isEditMode ? 'bg-muted border-0 cursor-default' : ''} ${
                validationErrors.reorderPoint ? 'border-red-500' : ''
              }`}
            />
            <p className="text-xs text-muted-foreground">Trigger automatic reorder</p>
            {validationErrors.reorderPoint && (
              <div className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                <AlertCircle className="h-3 w-3" />
                <span>{validationErrors.reorderPoint}</span>
              </div>
            )}
          </div>

          {/* Reorder Quantity */}
          <div className="space-y-2">
            <Label htmlFor="reorderQuantity">Reorder Quantity</Label>
            <Input
              id="reorderQuantity"
              type="number"
              value={editedItem.reorderQuantity}
              onChange={(e) => handleFieldChange("reorderQuantity", parseInt(e.target.value) || 0)}
              min="0"
              step="1"
              readOnly={!isEditMode}
              className={`${!isEditMode ? 'bg-muted border-0 cursor-default' : ''}`}
            />
            <p className="text-xs text-muted-foreground">Default order quantity</p>
          </div>
        </div>
      </div>
    </div>
  );
}
